/** Agency directory (who's who, who's working, who drives what) and the org chart editor. */
import { useEffect, useMemo, useState } from 'react';
import { ORG_KIND_LABEL, ROTATION_PRESETS, type OrgKind } from '@civilos/domain';
import { api } from '../shared/api.ts';
import { useAuth } from '../shared/auth.tsx';
import { Link } from '../shared/router.tsx';
import { Badge, Card, ErrorBox, Notice, PageHeader, Spinner, useAction, useLoad, useNarrow } from '../shared/ui.tsx';
import { at, EmptyState } from './common.tsx';

const carText = (c: any) => (c ? `${c.unit_number} · ${[c.year, c.color, c.make, c.model].filter(Boolean).join(' ')}${c.tag ? ` · ${c.tag}` : ''}` : null);
const t12 = (hhmm: string) => {
  const [h, m] = hhmm.split(':').map(Number) as [number, number];
  return `${((h + 11) % 12) + 1}${m ? `:${String(m).padStart(2, '0')}` : ''}${h < 12 ? 'a' : 'p'}`;
};

export function DirectoryPage() {
  const { can } = useAuth();
  const narrow = useNarrow();
  const [q, setQ] = useState('');
  const [shift, setShift] = useState<string | null>(null);
  const r = useLoad(() => api.get<any>('/api/directory'), []);
  const people: any[] = useMemo(() => {
    const s = q.trim().toLowerCase();
    return (r.data?.people ?? []).filter(
      (p: any) =>
        (!shift || (shift === 'on' ? p.onDuty : p.shiftId === shift)) &&
        (!s || [p.name, p.callNumber, p.phone, p.badge, p.unit, p.shift, p.supervisor, p.title, carText(p.car)].some((x) => x && String(x).toLowerCase().includes(s))),
    );
  }, [r.data, q, shift]);
  if (r.error) return <ErrorBox error={r.error} />;
  if (!r.data) return <Spinner />;
  const shifts: any[] = r.data.shifts;
  const on = shifts.filter((s) => s.onDuty);
  return (
    <div className="ops stack dir">
      <PageHeader
        crumb="Agency"
        title="Directory"
        sub="Phone, call number, shift, supervisor and the car each person is driving."
        actions={can('admin.org') ? <Link className="btn" to="/org">Edit org chart</Link> : undefined}
      />
      {shifts.length > 0 && (
        <div className="dir-shifts">
          <div className={`dir-now ${on.length ? '' : 'none'}`}>
            <span className="muted small">Working now · {new Date().toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}</span>
            <strong>{on.length ? on.map((s) => s.name).join(', ') : 'No shift on duty'}</strong>
            {on[0]?.supervisor && <span className="small">Supervisor: {on.map((s) => s.supervisor).filter(Boolean).join(', ')}</span>}
          </div>
          {shifts.map((s) => (
            <button key={s.id} type="button" className={`dir-shift ${s.onDuty ? 'on' : ''} ${shift === s.id ? 'sel' : ''}`} onClick={() => setShift(shift === s.id ? null : s.id)}>
              <strong>{s.name}</strong>
              <span className="small">
                {s.onDuty ? <Badge tone="ok" dot>On duty</Badge> : s.worksToday ? <Badge tone="info">Works today</Badge> : s.onDuty === false ? <span className="muted">Off today</span> : null}
              </span>
              <span className="small muted">
                {s.hours ? `${t12(s.hours.split('–')[0])}–${t12(s.hours.split('–')[1])}` : 'No rotation set'} · {s.members} people
              </span>
              {s.supervisor && <span className="small">{s.supervisor}</span>}
            </button>
          ))}
        </div>
      )}
      <div className="filterbar">
        <input type="search" className="rp-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Name, call number, phone, unit, car or tag" aria-label="Search the directory" autoFocus={!narrow} />
        {on.length > 0 && (
          <label className="check">
            <input type="checkbox" checked={shift === 'on'} onChange={(e) => setShift(e.target.checked ? 'on' : null)} /> Only who’s working now
          </label>
        )}
        <span className="spacer" />
        <span className="muted small">{people.length} people</span>
      </div>
      {!people.length ? (
        <EmptyState icon="directory" title="Nobody matches" />
      ) : narrow ? (
        <div className="dir-cards">
          {people.map((p) => (
            <div key={p.id} className="card dir-card">
              <div className="row">
                <strong>{p.name}</strong>
                {p.callNumber && <span className="dir-call">{p.callNumber}</span>}
                {p.onDuty && <Badge tone="ok" dot>On duty</Badge>}
              </div>
              <div className="small muted">{[p.title, p.shift ?? p.unit].filter(Boolean).join(' · ')}</div>
              {p.phone && (
                <a className="btn sm dir-phone" href={`tel:${p.phone.replace(/[^\d+]/g, '')}`}>
                  Call {p.phone}
                </a>
              )}
              {p.supervisor && <div className="small">Supervisor: {p.supervisor}</div>}
              {p.car && <div className="small">Car: {carText(p.car)}{p.car.spare && ' (spare)'}</div>}
            </div>
          ))}
        </div>
      ) : (
        <div className="card flush">
          <div className="table-wrap">
            <table className="table dir-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Call #</th>
                  <th>Phone</th>
                  <th>Shift / unit</th>
                  <th>Supervisor</th>
                  <th>Car</th>
                </tr>
              </thead>
              <tbody>
                {people.map((p) => (
                  <tr key={p.id}>
                    <td>
                      <strong>{p.name}</strong>
                      <div className="small muted">{[p.title, p.badge].filter(Boolean).join(' · ')}</div>
                    </td>
                    <td className="mono">{p.callNumber ?? <span className="muted">—</span>}</td>
                    <td>{p.phone ? <a href={`tel:${p.phone.replace(/[^\d+]/g, '')}`}>{p.phone}</a> : <span className="muted">—</span>}</td>
                    <td>
                      {p.shift ?? p.unit ?? <span className="muted">—</span>}
                      {p.unit && p.shift && p.unit !== p.shift && <div className="small muted">{p.unit}</div>}
                      {p.onDuty && (
                        <div>
                          <Badge tone="ok" dot>On duty</Badge>
                        </div>
                      )}
                    </td>
                    <td>{p.supervisor ?? <span className="muted">—</span>}</td>
                    <td>
                      {p.car ? (
                        <>
                          {carText(p.car)}
                          {p.car.spare && (
                            <div>
                              <Badge tone="info">Spare</Badge>
                            </div>
                          )}
                        </>
                      ) : (
                        <span className="muted">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Org chart (admins) ───────────────────────────────────────────────────────

/** Everyone who answered to one supervisor now answers to another (Capt. John retires → Capt. Rick). */
function SupervisorHandoff({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const r = useLoad(() => api.get<any>('/api/org/supervisors'), []);
  const [fromId, setFrom] = useState('');
  const [toId, setTo] = useState('');
  const [preview, setPreview] = useState<any>(null);
  const [done, setDone] = useState<any>(null);
  const a = useAction();
  useEffect(() => {
    setPreview(null);
    if (fromId && toId && fromId !== toId) a.run(async () => setPreview(await api.post('/api/org/supervisor-handoff', { fromId, toId, preview: true })));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromId, toId]);
  const sups: any[] = r.data?.supervisors ?? [];
  return (
    <Card title="Replace a supervisor" actions={<button className="btn link sm" onClick={onClose}>Close</button>}>
      {!r.data ? (
        <Spinner />
      ) : done ? (
        <div className="stack sm">
          <Notice tone="ok">
            Done. The {done.people.length} {done.people.length === 1 ? 'person' : 'people'} who answered to {done.from} now answer to {done.to}
            {done.units.length ? ` (${done.units.join(', ')})` : ''}.{done.reviews ? ` ${done.reviews} report${done.reviews === 1 ? '' : 's'} waiting for review moved too.` : ''}
            {done.narcan ? ` ${done.narcan} Narcan form${done.narcan === 1 ? '' : 's'} to verify moved too.` : ''}
          </Notice>
          <div>
            <button className="btn" onClick={() => (setDone(null), setFrom(''), setTo(''), r.reload())}>
              Replace another
            </button>
          </div>
        </div>
      ) : (
        <div className="stack sm">
          <p className="small muted">Everyone who answers to the first person will answer to the second: every shift and unit they head gets the new supervisor, and reports waiting on them for review and Narcan forms to verify move over too.</p>
          <div className="grid-2">
            <label>
              Everyone who answers to
              <select value={fromId} onChange={(e) => setFrom(e.target.value)} aria-label="Everyone who answers to">
                <option value="">Pick the supervisor being replaced…</option>
                {sups.map((x) => (
                  <option key={x.id} value={x.id}>
                    {x.name}
                    {x.active ? '' : ' (no longer active)'} — {x.people.length} {x.people.length === 1 ? 'person' : 'people'}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Now answers to
              <select value={toId} onChange={(e) => setTo(e.target.value)} aria-label="Now answers to">
                <option value="">Pick the new supervisor…</option>
                {(r.data.people as any[])
                  .filter((p) => p.id !== fromId)
                  .map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                      {p.title ? ` — ${p.title}` : ''}
                    </option>
                  ))}
              </select>
            </label>
          </div>
          {preview && (
            <div className="sv-preview">
              <strong>
                {preview.people.length} {preview.people.length === 1 ? 'person' : 'people'} will answer to {preview.to}
              </strong>
              {preview.units.length > 0 && <div className="small">Units: {preview.units.join(', ')}</div>}
              {preview.people.length > 0 && <div className="small muted sv-names">{preview.people.join(', ')}</div>}
              {(preview.reviews > 0 || preview.narcan > 0) && (
                <div className="small">
                  Also moves: {[preview.reviews && `${preview.reviews} report${preview.reviews === 1 ? '' : 's'} waiting for review`, preview.narcan && `${preview.narcan} Narcan form${preview.narcan === 1 ? '' : 's'} to verify`].filter(Boolean).join(' and ')}
                </div>
              )}
            </div>
          )}
          <ErrorBox error={a.error} />
          <div>
            <button
              className="btn primary"
              disabled={a.busy || !preview || (!preview.units.length && !preview.reviews && !preview.narcan)}
              onClick={() => a.run(async () => (setDone(await api.post('/api/org/supervisor-handoff', { fromId, toId })), onDone()))}
            >
              {preview ? `Move ${preview.people.length} ${preview.people.length === 1 ? 'person' : 'people'} to ${preview.to}` : 'Move them'}
            </button>
          </div>
        </div>
      )}
    </Card>
  );
}

export function OrgChartPage() {
  const r = useLoad(() => api.get<any>('/api/org'), []);
  const [edit, setEdit] = useState<any | null>(null);
  const [handoff, setHandoff] = useState(new URLSearchParams(location.search).get('handoff') === '1');
  if (r.error) return <ErrorBox error={r.error} />;
  if (!r.data) return <Spinner />;
  const units: any[] = r.data.units;
  const people: any[] = r.data.people;
  const name = (id: string | null) => people.find((p) => p.id === id)?.display_name ?? null;
  const kids = (pid: string | null) => units.filter((u) => u.parent_id === pid && !u.archived);
  const members = (uid: string) => people.filter((p) => p.unit_id === uid);
  const Node = ({ u }: { u: any }) => (
    <li>
      <div className={`org-node k-${u.kind}`}>
        <div className="row">
          <strong>{u.name}</strong>
          <span className="muted small">{ORG_KIND_LABEL[u.kind as OrgKind]}</span>
          {u.onDuty === true && <Badge tone="ok" dot>On duty</Badge>}
          <span className="spacer" />
          {r.data.canManage && (
            <button className="btn link sm" onClick={() => setEdit(u)}>
              Edit
            </button>
          )}
        </div>
        <div className="small">Supervisor: {name(u.supervisor_id) ?? <span className="muted">none set</span>}</div>
        {u.rotation && (
          <div className="small muted">
            {ROTATION_PRESETS.find((p) => p.pattern === u.rotation.pattern)?.label ?? `${u.rotation.pattern.length}-day rotation`} · {u.rotation.start}–{u.rotation.end}
          </div>
        )}
        {members(u.id).length > 0 && <div className="org-people">{members(u.id).map((p) => <span key={p.id}>{p.display_name}{p.call_number ? ` (${p.call_number})` : ''}</span>)}</div>}
      </div>
      {kids(u.id).length > 0 && (
        <ul>
          {kids(u.id).map((k) => (
            <Node key={k.id} u={k} />
          ))}
        </ul>
      )}
    </li>
  );
  return (
    <div className="ops stack">
      <PageHeader
        crumb="Agency"
        title="Org chart"
        sub="Divisions, shifts and units, who supervises each, and who’s in them. Overdue messages go to the person’s supervisor here."
        actions={
          r.data.canManage && (
            <div className="row gap-sm">
              <button className="btn" onClick={() => setHandoff(!handoff)}>
                Replace a supervisor
              </button>
              <button className="btn primary" onClick={() => setEdit({ name: '', kind: 'shift', parent_id: null, supervisor_id: null, rotation: null })}>
                Add unit
              </button>
            </div>
          )
        }
      />
      {handoff && <SupervisorHandoff onClose={() => setHandoff(false)} onDone={r.reload} />}
      {edit && <UnitEditor u={edit} units={units} people={people} onClose={() => setEdit(null)} onSaved={() => (setEdit(null), r.reload())} />}
      {units.length === 0 ? (
        <EmptyState icon="org" title="No org chart yet">
          Start with a division (Patrol), then add its shifts (A, B, C, D) and units.
        </EmptyState>
      ) : (
        <ul className="org-tree">
          {kids(null).map((u) => (
            <Node key={u.id} u={u} />
          ))}
        </ul>
      )}
      <Card title="People">
        <p className="small muted">Put each person in their shift or unit, and set their radio call number and work phone.</p>
        <PeopleTable people={people} units={units.filter((u) => !u.archived)} canManage={r.data.canManage} onSaved={r.reload} />
      </Card>
    </div>
  );
}

function UnitEditor({ u, units, people, onClose, onSaved }: { u: any; units: any[]; people: any[]; onClose: () => void; onSaved: () => void }) {
  const [name, setName] = useState(u.name);
  const [kind, setKind] = useState<OrgKind>(u.kind);
  const [parent, setParent] = useState<string>(u.parent_id ?? '');
  const [sup, setSup] = useState<string>(u.supervisor_id ?? '');
  const [rot, setRot] = useState<{ anchor: string; pattern: string; start: string; end: string } | null>(u.rotation);
  const a = useAction();
  const save = (archived?: boolean) =>
    a.run(async () => {
      const body = { name, kind, parentId: parent || null, supervisorId: sup || null, rotation: kind === 'shift' ? rot : null, ...(archived ? { archived: true } : {}) };
      if (u.id) await api.post(`/api/org/units/${u.id}`, body);
      else await api.post('/api/org/units', body);
      onSaved();
    });
  return (
    <div className="card stack org-edit" role="dialog" aria-label={u.id ? `Edit ${u.name}` : 'Add unit'}>
      <h3>{u.id ? `Edit ${u.name}` : 'Add a division, shift or unit'}</h3>
      <div className="grid-3">
        <label>
          Name
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="A Shift" />
        </label>
        <label>
          Kind
          <select value={kind} onChange={(e) => setKind(e.target.value as OrgKind)}>
            {Object.entries(ORG_KIND_LABEL).map(([k, l]) => (
              <option key={k} value={k}>
                {l}
              </option>
            ))}
          </select>
        </label>
        <label>
          Part of
          <select value={parent} onChange={(e) => setParent(e.target.value)}>
            <option value="">— top level —</option>
            {units.filter((x) => x.id !== u.id && !x.archived).map((x) => (
              <option key={x.id} value={x.id}>
                {x.name}
              </option>
            ))}
          </select>
        </label>
        <label>
          Supervisor
          <select value={sup} onChange={(e) => setSup(e.target.value)}>
            <option value="">— none —</option>
            {people.map((p) => (
              <option key={p.id} value={p.id}>
                {p.display_name}
              </option>
            ))}
          </select>
        </label>
      </div>
      {kind === 'shift' && (
        <fieldset className="org-rot">
          <legend>Rotation (which days this shift works)</legend>
          <div className="row gap-sm wrap">
            {ROTATION_PRESETS.map((p) => (
              <button key={p.key} type="button" className={`btn sm ${rot?.pattern === p.pattern && rot?.start === p.start ? 'primary' : ''}`} onClick={() => setRot({ anchor: rot?.anchor ?? new Date().toLocaleDateString('en-CA'), pattern: p.pattern, start: p.start, end: p.end })}>
                {p.label}
              </button>
            ))}
            {rot && (
              <button type="button" className="btn sm ghost" onClick={() => setRot(null)}>
                No rotation
              </button>
            )}
          </div>
          {rot && (
            <div className="grid-4">
              <label>
                First day of the cycle
                <input type="date" value={rot.anchor} onChange={(e) => setRot({ ...rot, anchor: e.target.value })} />
              </label>
              <label>
                Pattern (1 = works)
                <input className="mono" value={rot.pattern} onChange={(e) => setRot({ ...rot, pattern: e.target.value.replace(/[^01]/g, '') })} />
              </label>
              <label>
                Starts
                <input type="time" value={rot.start} onChange={(e) => setRot({ ...rot, start: e.target.value })} />
              </label>
              <label>
                Ends
                <input type="time" value={rot.end} onChange={(e) => setRot({ ...rot, end: e.target.value })} />
              </label>
            </div>
          )}
          {rot && <RotationPreview rot={rot} />}
        </fieldset>
      )}
      <div className="row gap-sm">
        <button className="btn primary" disabled={!name.trim() || a.busy} onClick={() => save()}>
          Save
        </button>
        <button className="btn ghost" onClick={onClose}>
          Cancel
        </button>
        <span className="spacer" />
        {u.id && (
          <button className="btn danger ghost" disabled={a.busy} onClick={() => save(true)}>
            Remove unit
          </button>
        )}
      </div>
      <ErrorBox error={a.error} />
    </div>
  );
}

function RotationPreview({ rot }: { rot: { anchor: string; pattern: string } }) {
  const days = Array.from({ length: 28 }, (_, i) => {
    const d = new Date(Date.now() + i * 86_400_000);
    const iso = d.toLocaleDateString('en-CA');
    const n = rot.pattern.length;
    const idx = Math.round((Date.parse(`${iso}T12:00:00Z`) - Date.parse(`${rot.anchor}T12:00:00Z`)) / 86_400_000);
    return { d, works: n > 0 && rot.pattern[((idx % n) + n) % n] === '1' };
  });
  return (
    <div className="rot-prev" aria-label="Next four weeks">
      {days.map((x, i) => (
        <span key={i} className={x.works ? 'on' : ''} title={x.d.toDateString()}>
          {x.d.toLocaleDateString('en-US', { weekday: 'narrow' })}
          <b>{x.d.getDate()}</b>
        </span>
      ))}
    </div>
  );
}

function PeopleTable({ people, units, canManage, onSaved }: { people: any[]; units: any[]; canManage: boolean; onSaved: () => void }) {
  return (
    <div className="table-wrap">
      <table className="table org-people-t">
        <thead>
          <tr>
            <th>Name</th>
            <th>Shift / unit</th>
            <th>Call #</th>
            <th>Work phone</th>
          </tr>
        </thead>
        <tbody>
          {people.map((p) => (
            <PersonRow key={p.id} p={p} units={units} canManage={canManage} onSaved={onSaved} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function PersonRow({ p, units, canManage, onSaved }: { p: any; units: any[]; canManage: boolean; onSaved: () => void }) {
  const [call, setCall] = useState(p.call_number ?? '');
  const [phone, setPhone] = useState(p.phone ?? '');
  const a = useAction();
  useEffect(() => (setCall(p.call_number ?? ''), setPhone(p.phone ?? '')), [p.call_number, p.phone]);
  const save = (body: Record<string, unknown>) => a.run(async () => (await api.post(`/api/org/people/${p.id}`, body), onSaved()));
  return (
    <tr>
      <td>
        {p.display_name}
        <div className="small muted">{p.title}</div>
        {a.error ? <ErrorBox error={a.error} /> : null}
      </td>
      <td>
        <select aria-label={`Unit for ${p.display_name}`} disabled={!canManage} value={p.unit_id ?? ''} onChange={(e) => save({ unitId: e.target.value || null })}>
          <option value="">—</option>
          {units.map((u) => (
            <option key={u.id} value={u.id}>
              {u.name}
            </option>
          ))}
        </select>
      </td>
      <td>
        <input className="mono w-call" aria-label={`Call number for ${p.display_name}`} disabled={!canManage} value={call} onChange={(e) => setCall(e.target.value)} onBlur={() => call !== (p.call_number ?? '') && save({ callNumber: call || null })} />
      </td>
      <td>
        <input aria-label={`Phone for ${p.display_name}`} disabled={!canManage} value={phone} onChange={(e) => setPhone(e.target.value)} onBlur={() => phone !== (p.phone ?? '') && save({ phone: phone || null })} />
      </td>
    </tr>
  );
}

export const _at = at;
